import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Play, Square, Loader2, ChevronDown, TrendingUp, TrendingDown, CandlestickChart, ArrowLeft, Settings2, Info,
} from "lucide-react";

const STRATS = ["Soroscalper", "Trend Rider", "Reversal Pro", "Momentum Burst"];

export default function PocketBot() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const tickRef = useRef(null);
  const runningRef = useRef(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/pocket/state"); setSt(data); runningRef.current = data.running; } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-generate trades while running.
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(async () => {
      if (!runningRef.current) return;
      try {
        const { data } = await api.post("/pocket/tick");
        if (data.trade) {
          setSt((p) => p ? { ...p, balance: data.balance, session_profit: data.session_profit, trades: [data.trade, ...(p.trades || [])].slice(0, 30) } : p);
        }
        if (data.target_reached) { runningRef.current = false; setSt((p) => p ? { ...p, running: false } : p); toast.success("🎯 Profit target reached — bot stopped."); }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(tickRef.current);
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pocket/toggle");
      runningRef.current = data.running;
      setSt((p) => p ? { ...p, running: data.running, session_profit: data.session_profit } : p);
      toast.success(data.running ? "Pocket Bot started (simulator)" : "Pocket Bot stopped");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveCfg = async (patch) => {
    setSt((p) => p ? { ...p, settings: { ...p.settings, ...patch } } : p);
    try { await api.post("/pocket/settings", patch); } catch { /* ignore */ }
  };

  if (!st) return <div className="min-h-screen bg-[#0f0620] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-purple-400" /></div>;

  const s = st.settings || {};
  const running = st.running;
  const profit = st.session_profit || 0;

  return (
    <div className="min-h-screen bg-[#0f0620] text-white pb-10" data-testid="pocket-bot-page"
      style={{ backgroundImage: "radial-gradient(1200px 500px at 50% -10%, rgba(139,92,246,0.18), transparent)" }}>
      <div className="max-w-[480px] md:max-w-3xl mx-auto px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="pocket-back" className="text-purple-300 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center"><CandlestickChart className="h-5 w-5" /></div>
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold leading-none">Pocket Bot</h1>
            <span className="text-[11px] text-purple-300/80">Auto Trading · {s.theme}</span>
          </div>
        </div>

        {/* Simulator disclaimer */}
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5" data-testid="pocket-sim-badge">
          <Info className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200/90 leading-relaxed">
            <b>SIMULATOR</b> — Pocket Option has no official API. This bot demonstrates the strategy &amp; UI with simulated trades. It does not place real orders on Pocket Option.
          </p>
        </div>

        {/* Real-trading client guide */}
        <button onClick={() => setShowGuide((v) => !v)} data-testid="pocket-guide-toggle"
          className="mt-3 w-full flex items-center justify-between rounded-xl border border-purple-500/30 bg-purple-500/10 px-3 py-3 text-left">
          <span className="flex items-center gap-2 text-[13px] font-semibold text-purple-200">
            <Info className="h-4 w-4" /> Kijan pou fè VRE trade Pocket Option?
          </span>
          <ChevronDown className={`h-4 w-4 text-purple-300 transition-transform ${showGuide ? "rotate-180" : ""}`} />
        </button>
        {showGuide && (
          <div className="mt-2 rounded-xl border border-purple-500/20 bg-[#1b0f33] p-4 space-y-3 text-[12.5px] leading-relaxed text-purple-100/90" data-testid="pocket-guide-body">
            <p className="text-amber-200/90">
              ⚠️ Vre trading Pocket Option <b>PA KA</b> fèt sou telefòn ni sou entènèt sa a — Pocket Option
              bloke sèvè cloud yo. Li dwe kouri sou yon <b>PC/laptop</b> oswa yon <b>VPS rezidansyèl</b>.
            </p>
            <div>
              <div className="font-semibold text-white mb-1">📥 Etap 1 — Jwenn pwogram nan</div>
              Telechaje katab <b>pocket_pc_bot</b> la (mande administratè w la, oswa nan GitHub) sou PC ou.
            </div>
            <div>
              <div className="font-semibold text-white mb-1">💻 Etap 2 — Kouri l</div>
              Enstale <b>Python 3.10+</b>, epi double-click <b>run.bat</b> (Windows). Yon paj ap louvri
              sou <span className="text-purple-300">http://127.0.0.1:8777</span>.
            </div>
            <div>
              <div className="font-semibold text-white mb-1">🔑 Etap 3 — SSID + Demo/Real</div>
              Sou Pocket Option (navigatè): chwazi <b>Demo</b> oswa <b>Real</b> anlè adwat. Peze <b>F12 →
              Application → Cookies</b>, kopye valè <b>ssid</b>. Kole l nan pwogram nan → <b>Connect</b>.
              Bot la ap montre DEMO oswa REAL otomatik.
            </div>
            <div>
              <div className="font-semibold text-white mb-1">▶️ Etap 4 — Trade</div>
              Chwazi Asset, Montan, Expiry, estrateji → peze <b>Start</b>. Kite fenèt la louvri.
            </div>
            <div className="pt-1 border-t border-purple-500/20">
              <div className="font-semibold text-white mb-1">📱 Pou itilize l sou TELEFÒN (via VPS)</div>
              Sou telefòn, pa gen anyen k ap trade — telefòn nan se jis yon <b>kontwòl adistans</b> (kòm yon remot TV):
              <ul className="list-disc ml-4 mt-1 space-y-0.5 text-purple-200/85">
                <li>🖥️ Bot la kouri sou yon <b>VPS rezidansyèl</b> (li ki fè vre trade yo)</li>
                <li>📱 Kliyan ouvri <span className="text-purple-300">http://IP_VPS:8777</span> nan navigatè telefòn</li>
                <li>📱 Li mete <b>modpas</b>, kole <b>SSID</b>, peze <b>Start</b></li>
                <li>🖥️ VPS la fè trade yo · 📱 telefòn nan jis montre rezilta yo</li>
              </ul>
              <div className="mt-1">Gade gid <b>VPS_GUIDE.md</b> nan katab la pou tout etap yo.</div>
            </div>
            <p className="text-[11px] text-purple-300/70">💡 Konsèy: teste ak yon kont DEMO an premye.</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2.5 mt-4">
          {[
            { l: "Balance", v: `$${(st.balance || 0).toFixed(2)}`, c: "text-white" },
            { l: "Session P/L", v: `${profit >= 0 ? "+" : ""}$${profit.toFixed(2)}`, c: profit >= 0 ? "text-emerald-400" : "text-red-400" },
            { l: "Target", v: `$${Number(s.expected_profit || 0).toFixed(2)}`, c: "text-purple-300" },
          ].map((x) => (
            <div key={x.l} className="rounded-2xl border border-purple-500/20 bg-[#1b0f33] p-3">
              <div className="text-[10px] uppercase tracking-widest text-purple-300/70">{x.l}</div>
              <div className={`font-heading text-lg font-bold mt-0.5 ${x.c}`}>{x.v}</div>
            </div>
          ))}
        </div>

        {/* Start/Stop */}
        <button onClick={toggle} disabled={busy} data-testid="pocket-start-stop"
          className="w-full h-16 mt-4 rounded-2xl flex items-center justify-center gap-2 font-bold text-lg transition-transform duration-200 hover:scale-[1.02] active:scale-95"
          style={{ backgroundColor: running ? "#ef4444" : "#8b5cf6", boxShadow: running ? "0 0 28px rgba(239,68,68,0.45)" : "0 0 28px rgba(139,92,246,0.5)" }}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (<>{running ? <Square className="h-5 w-5" fill="white" /> : <Play className="h-5 w-5" fill="white" />}{running ? "STOP BOT" : "START BOT"}</>)}
        </button>

        {/* Market Control */}
        <button onClick={() => setShowCfg((v) => !v)} data-testid="pocket-config-toggle"
          className="w-full mt-4 flex items-center justify-between rounded-2xl border border-purple-500/20 bg-[#1b0f33] px-4 py-3.5">
          <span className="flex items-center gap-2 font-semibold"><Settings2 className="h-4 w-4 text-purple-300" /> Market Control</span>
          <ChevronDown className={`h-4 w-4 text-purple-300 transition-transform ${showCfg ? "rotate-180" : ""}`} />
        </button>
        {showCfg && (
          <div className="mt-2.5 rounded-2xl border border-purple-500/20 bg-[#160c2b] p-4 space-y-4 fade-up" data-testid="pocket-config">
            <Field label="Strategy">
              <select value={s.strategy} onChange={(e) => saveCfg({ strategy: e.target.value })} data-testid="pocket-strategy"
                className="cfg-input">{STRATS.map((x) => <option key={x} value={x}>{x}</option>)}</select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Entry value ($)"><input type="number" min="1" step="1" value={s.entry_value} onChange={(e) => saveCfg({ entry_value: Number(e.target.value) })} data-testid="pocket-entry" className="cfg-input" /></Field>
              <Field label="Profit target ($)"><input type="number" min="1" step="1" value={s.expected_profit} onChange={(e) => saveCfg({ expected_profit: Number(e.target.value) })} data-testid="pocket-target" className="cfg-input" /></Field>
              <Field label="Gale levels"><input type="number" min="0" max="9" value={s.gale_levels} onChange={(e) => saveCfg({ gale_levels: Number(e.target.value) })} className="cfg-input" /></Field>
              <Field label="Soros levels"><input type="number" min="0" max="5" value={s.soros_levels} onChange={(e) => saveCfg({ soros_levels: Number(e.target.value) })} className="cfg-input" /></Field>
              <Field label="Max operations"><input type="number" min="1" max="8" value={s.max_ops} onChange={(e) => saveCfg({ max_ops: Number(e.target.value) })} className="cfg-input" /></Field>
              <Field label="Direction">
                <select value={s.direction} onChange={(e) => saveCfg({ direction: e.target.value })} className="cfg-input">
                  <option>all directions</option><option>calls only</option><option>puts only</option></select>
              </Field>
            </div>
          </div>
        )}

        {/* Trades table */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-purple-300/70 mb-2 flex items-center gap-1.5"><TrendingUp className="h-3 w-3" /> Live Operations</div>
          <div className="rounded-2xl border border-purple-500/20 bg-[#160c2b] overflow-hidden" data-testid="pocket-trades">
            <div className="grid grid-cols-[1fr_1.2fr_0.9fr_0.9fr] gap-1 px-3 py-2.5 text-[10px] uppercase tracking-wider text-purple-300/60 border-b border-purple-500/15">
              <span>Time</span><span>Asset · Dir</span><span>Value</span><span className="text-right">Profit</span>
            </div>
            {(st.trades || []).length === 0 ? (
              <p className="text-center text-purple-300/50 text-sm py-8">No operations yet. Press START.</p>
            ) : (st.trades || []).map((t) => (
              <div key={t.id} data-testid="pocket-trade-row" className="grid grid-cols-[1fr_1.2fr_0.9fr_0.9fr] gap-1 px-3 py-2.5 text-xs border-b border-purple-500/10 items-center">
                <span className="text-purple-200/70 font-mono-term">{t.time}</span>
                <span className="flex items-center gap-1 font-medium">
                  {t.direction === "CALL" ? <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> : <TrendingDown className="h-3.5 w-3.5 text-red-400" />}
                  {t.asset}
                </span>
                <span className="text-purple-200/70">${t.value}</span>
                <span className={`text-right font-semibold ${t.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.profit >= 0 ? "+" : ""}${t.profit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`.cfg-input{width:100%;height:42px;border-radius:12px;background:#0f0620;border:1px solid rgba(139,92,246,0.25);color:#fff;font-size:13px;padding:0 12px;outline:none}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-purple-300/60">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);
